import { useState, useRef, useContext, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FiMoreVertical } from "react-icons/fi";
import { Popup } from "@progress/kendo-react-popup";
import { inAndOut } from "ol/easing";
import { getCenter } from "ol/extent";
import MapContext from "../../../MapContext";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { DeleteRelatedRecordData } from "../../../Editing/editUtils";
import {
    editRelatedRecord,
    clearRelatedRecordEdits,
} from "../../../../redux/editingSlice";
import { determineSize } from "./FeatureDescription";
import jwt_decode from "jwt-decode";
import { getCookie } from "../../../../api/axios";
import { makeArray } from "../../../../permissionUtils";
import { setActiveTool } from "../../../../redux/toolSelectSlice";
import { getMapLayerById, getFeatureWithId } from "../../../utils/mapUtils";
import { setRelatedRecordData } from "../../../../redux/resultsSlice";
const ratio = 50;
const RelatedRecordPopup = ({ feature, layerId, relatedRecordInfo, data, setData }) => {
    const relatedRecordsData = useSelector(
        (state) => state.resultsData.relatedRecordsData
    );
    const [show, setShow] = useState(false);
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const popupAnchor = useRef();
    const popupRef = useRef();
    const { map } = useContext(MapContext);
    const [deleteErrorMessage, setDeleteErrorMessage] = useState("");
    const mapConfig = useSelector((state) => state.configData.mapConfig);
    let lyr = useSelector((state) =>
        state.configData.vectorLayerProperties.find((l) => l.Id === layerId)
    );
    const dispatch = useDispatch();

    const token = getCookie("ast");

    let userGivenName = "Guest";
    let tokenRoles = ["GUEST"];
    if (token) {
        userGivenName = jwt_decode(token).preferred_username;
        tokenRoles = jwt_decode(token).realm_access.roles;
    }

    const handlePopup = () => {
        setShow(!show);
    };
    const detectClick = (e) => {
        if (
            !popupRef.current.contains(e.target) &&
            !popupAnchor.current.contains(e.target)
        ) {
            setShow(false);
        }
    };
    useEffect(() => {
        if (show) {
            document.addEventListener("mousedown", detectClick);
        }
        return () => {
            document.removeEventListener("mousedown", detectClick);
        };
    }, [show]);
    const handleDeleteOptions = () => {
        setShowDeleteConfirmation(true);
        setDeleteErrorMessage();
        setShow(false);
    };
    const closeDeleteConfirmation = () => {
        setShowDeleteConfirmation(false);
        setDeleteErrorMessage();
    };
    const onDeleteData = async () => {
        DeleteRelatedRecordData(
            feature.id_,
            lyr,
            relatedRecordInfo,
            data[relatedRecordInfo.PrimaryKey],
            (evt) => {
                if (evt.error) {
                    setDeleteErrorMessage(evt.error);
                } else {
                    setShowDeleteConfirmation(false);
                    setDeleteErrorMessage("");
                    setData(undefined);
                    // Remove feature from related records data
                    let temp = { ...relatedRecordsData };
                    let temp2 = temp.data[relatedRecordInfo.RelatedTableID].filter(
                        (item) =>
                            item[relatedRecordInfo.PrimaryKey] !==
                            data[relatedRecordInfo.PrimaryKey]
                    );
                    // Create a new object with the desired changes
                    let newData = {
                        ...temp.data,
                        [relatedRecordInfo.RelatedTableID]: temp2,
                    };
                    // Create a new object with the updated data
                    dispatch(setRelatedRecordData({ ...temp, data: newData }));
                    // Refresh layers to unshow removed feature
                    const mapLayer = getMapLayerById(map, layerId).getSource();
                    mapLayer.refresh();
                    const layerLabel = getMapLayerById(map, layerId + "_label");
                    if (layerLabel) {
                        layerLabel.getSource().refresh();
                    }
                }
            }
        );
    };
    const handleEditOptions = () => {
        dispatch(clearRelatedRecordEdits());
        dispatch(
            editRelatedRecord({
                RelatedRecordInfo: {
                    relatedRecordInfo: relatedRecordInfo,
                    layerInfo: lyr,
                },
                RelatedRecordData: { record: data },
            })
        );
        dispatch(setActiveTool("editrelatedrecord"));

        setShow(false);
    };

    const handlePanToFeature = () => {
        const geometry = feature.getGeometry();
        const extent = geometry.getExtent();
        const view = map.getView();
        if (geometry.getType() === "Point") {
            const zoom = view.getZoom();
            view.animate({
                center: getCenter(extent),
                zoom: zoom < 20 ? 20 : undefined,
            });
        } else {
            view.fit(extent, {
                size: determineSize(map, ratio),
                duration: 750,
                easing: inAndOut,
            });
        }
        setShow(false);
    };
    const relatedRecordsPermissionsEditFilter = (relatedRecordConfig, permissions) => {
        if (!relatedRecordConfig) return;
        return makeArray(relatedRecordConfig?.Permissions?.Permission).some((l) => {
            return (
                permissions.includes(l?.Value) &&
                l?.Read === true &&
                l?.UpdateRecord === true
            );
        });
    };

    const relatedRecordsPermissionsDeleteFilter = (relatedRecordConfig, permissions) => {
        if (!relatedRecordConfig) return;

        return makeArray(relatedRecordConfig?.Permissions?.Permission).some((l) => {
            return (
                permissions.includes(l?.Value) &&
                l?.Read === true &&
                l?.DeleteRecord === true
            );
        });
    };
    return (
        <>
            {showDeleteConfirmation && !deleteErrorMessage && (
                <Dialog
                    title={"Delete Data"}
                    onClose={closeDeleteConfirmation}
                    width={350}
                    id="delete-data-dialog">
                    <div>Are you sure you want to permanently delete this feature?</div>
                    <DialogActionsBar layout={"stretched"}>
                        <button
                            className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-primary"
                            onClick={closeDeleteConfirmation}>
                            Cancel
                        </button>
                        <button
                            className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-base"
                            onClick={onDeleteData}>
                            Delete
                        </button>
                    </DialogActionsBar>
                </Dialog>
            )}
            {showDeleteConfirmation && deleteErrorMessage && (
                <Dialog
                    title={"Error Deleting Data"}
                    onClose={closeDeleteConfirmation}
                    width={350}>
                    <div>{deleteErrorMessage}</div>
                    <DialogActionsBar layout={"stretched"}>
                        <button
                            className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-primary"
                            onClick={closeDeleteConfirmation}>
                            Close
                        </button>
                    </DialogActionsBar>
                </Dialog>
            )}
            <div
                style={{ width: "20px", height: "20px" }}
                onClick={handlePopup}
                ref={popupAnchor}
                className="layer-options-button">
                <FiMoreVertical />
            </div>
            <Popup
                anchor={popupAnchor.current}
                show={show}
                className="layer-options-popup">
                <div ref={popupRef}>
                    {" "}
                    {
                        <>
                            {relatedRecordsPermissionsEditFilter(
                                relatedRecordInfo,
                                tokenRoles
                            ) && (
                                <div className="popup-option" onClick={handleEditOptions}>
                                    <div className="popup-label">Edit Record</div>
                                    <div>Edit this Record.</div>
                                </div>
                            )}
                        </>
                    }
                    {
                        <>
                            {relatedRecordsPermissionsDeleteFilter(
                                relatedRecordInfo,
                                tokenRoles
                            ) && (
                                <div
                                    className="popup-option"
                                    onClick={handleDeleteOptions}>
                                    <div className="popup-label">Delete Record</div>
                                    <div>Delete the current Record</div>
                                </div>
                            )}
                        </>
                    }
                    <div className="popup-option" onClick={handlePanToFeature}>
                        <div className="popup-label">Pan</div>
                        <div>Pan the map to center this feature</div>
                    </div>
                </div>
            </Popup>
        </>
    );
};

export default RelatedRecordPopup;
